import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Form, Modal, Alert, Badge } from 'react-bootstrap';
import { Formik } from 'formik';
import * as Yup from 'yup';
import projectService from '../../services/projectService';
import userService from '../../services/userService';
import ProjectFiles from '../../components/admin/ProjectFiles';
import ProjectUrls from '../../components/admin/ProjectUrls';

const ManageProjects = () => {
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [developers, setDevelopers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [currentProject, setCurrentProject] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);
  const [actionSuccess, setActionSuccess] = useState('');
  const [expandedProjects, setExpandedProjects] = useState({});
  const [searchTerm, setSearchTerm] = useState('');

  // Validation schema for project form
  const projectSchema = Yup.object({
    name: Yup.string().required('Project name is required'),
    description: Yup.string(),
    developers: Yup.array().of(Yup.number())
  });

  // Fetch projects and developers on component mount
  useEffect(() => {
    const fetchData = async () => {
      await fetchProjects();
      await fetchDevelopers();
    };
    
    fetchData();
  }, []);

  // Fetch all projects
  const fetchProjects = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await projectService.getAllProjects();
      setProjects(data);
      setFilteredProjects(data);
    } catch (err) {
      setError('Failed to load projects. Please try again.');
      console.error('Error fetching projects:', err);
    } finally {
      setLoading(false);
    }
  };
  
  // Filter projects based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredProjects(projects);
    } else {
      const filtered = projects.filter(project => 
        project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (project.description && project.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (project.developers && project.developers.some(dev => 
          dev.name.toLowerCase().includes(searchTerm.toLowerCase())
        ))
      );
      setFilteredProjects(filtered);
    }
  }, [searchTerm, projects]);
  
  // Fetch all developers
  const fetchDevelopers = async () => {
    try {
      const data = await userService.getAllDevelopers();
      setDevelopers(data);
    } catch (err) {
      console.error('Error fetching developers:', err);
    }
  };

  // Handle opening the edit modal
  const handleEditClick = (project) => {
    setCurrentProject(project);
    setShowEditModal(true);
  };

  // Handle opening the delete confirmation
  const handleDeleteClick = (project) => {
    setDeleteConfirmation(project);
  };

  // Toggle expanded state for a project
  const toggleExpandProject = (projectId) => {
    setExpandedProjects(prev => ({
      ...prev,
      [projectId]: !prev[projectId]
    }));
  };

  // Handle project deletion
  const confirmDelete = async () => {
    if (!deleteConfirmation) return;
    
    try {
      await projectService.deleteProject(deleteConfirmation.id);
      setProjects(projects.filter(p => p.id !== deleteConfirmation.id));
      setActionSuccess(`Project "${deleteConfirmation.name}" has been deleted.`);
      setDeleteConfirmation(null);
      
      // Clear success message after 3 seconds
      setTimeout(() => setActionSuccess(''), 3000);
    } catch (err) {
      setError('Failed to delete project. Please try again.');
      console.error('Error deleting project:', err);
    }
  };

  // Handle project creation
  const handleCreateProject = async (values, { resetForm }) => {
    try {
      console.log('Creating project with values:', values);
      console.log('Token from localStorage:', localStorage.getItem('token'));
      
      // Make sure developers is an array
      if (!Array.isArray(values.developers)) {
        console.log('Developers is not an array, fixing it');
        values.developers = values.developers ? [values.developers] : [];
      }
      
      // Create a clean object with just the needed properties
      const projectData = {
        name: values.name,
        description: values.description,
        developers: values.developers
      };
      
      console.log('Sending project data:', projectData);
      
      const response = await projectService.createProject(projectData);
      console.log('Project creation response:', response);
      
      await fetchProjects();
      setShowAddModal(false);
      resetForm();
      setActionSuccess('Project created successfully!');
      
      // Clear success message after 3 seconds
      setTimeout(() => setActionSuccess(''), 3000);
    } catch (err) {
      setError('Failed to create project. Please try again.');
      console.error('Error creating project:', err);
      
      // Log more detailed error information
      if (err.response) {
        console.error('Error response data:', err.response.data);
        console.error('Error response status:', err.response.status);
        console.error('Error response headers:', err.response.headers);
      } else if (err.request) {
        console.error('Error request:', err.request);
      } else {
        console.error('Error message:', err.message);
      }
      console.error('Error config:', err.config);
    }
  };

  // Handle project update
  const handleUpdateProject = async (values) => {
    try {
      await projectService.updateProject(currentProject.id, values);
      await fetchProjects();
      setShowEditModal(false);
      setActionSuccess('Project updated successfully!');
      
      // Clear success message after 3 seconds
      setTimeout(() => setActionSuccess(''), 3000);
    } catch (err) {
      setError('Failed to update project. Please try again.');
      console.error('Error updating project:', err);
    }
  };

  return (
    <div className="page-fade-in">
      <Container>
        {/* Search Bar with Add New Project Button */}
        <Card className="mb-3 shadow-sm" style={{ border: 'none', borderRadius: 12, position: 'sticky', top: '75px', zIndex: 900 }}>
          <Card.Body style={{ padding: '1rem' }}>
            <div className="d-flex gap-2">
              <div className="input-group" style={{ maxWidth: '600px' }}>
                <span className="input-group-text bg-white border-end-0" style={{ borderRadius: '10px 0 0 10px', border: '2px solid #e9ecef', borderRight: 'none' }}>
                  <i className="bi bi-search" style={{ color: '#7C6FA0' }}></i>
                </span>
                <Form.Control
                  type="text"
                  placeholder="Search projects by name, description, or developer..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="border-start-0 ps-0"
                  style={{
                    borderRadius: '0 10px 10px 0',
                    border: '2px solid #e9ecef',
                    borderLeft: 'none',
                    boxShadow: 'none',
                    fontSize: '0.95rem'
                  }}
                />
                {searchTerm && (
                  <Button
                    variant="link"
                    onClick={() => setSearchTerm('')}
                    style={{ position: 'absolute', right: '10px', zIndex: 10, color: '#6c757d' }}
                  >
                    <i className="bi bi-x-circle"></i>
                  </Button>
                )}
              </div>
              <Button
                variant="primary"
                onClick={() => setShowAddModal(true)}
                style={{ borderRadius: 50, padding: '0.45rem 1.2rem' }}
              >
                <i className="bi bi-plus-circle me-2"></i>
                Add New Project
              </Button>
            </div>
            {searchTerm && (
              <small className="text-muted mt-2 d-block">
                Found {filteredProjects.length} project{filteredProjects.length !== 1 ? 's' : ''}
              </small>
            )}
          </Card.Body>
        </Card>

      {actionSuccess && (
        <Alert variant="success" onClose={() => setActionSuccess('')} dismissible>
          {actionSuccess}
        </Alert>
      )}

      {error && (
        <Alert variant="danger" onClose={() => setError('')} dismissible>
          {error}
        </Alert>
      )}

      <Card style={{ border: '1px solid #EEEEEE', borderRadius: 14 }}>
        <Card.Body>
          {loading ? (
            <div className="text-center p-4">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2" style={{ color: '#9E9E9E' }}>Loading projects...</p>
            </div>
          ) : filteredProjects.length === 0 ? (
            <div className="text-center p-4">
              {searchTerm ? (
                <>
                  <i className="bi bi-search" style={{ fontSize: '3rem', color: '#dee2e6' }}></i>
                  <p className="mt-3">No projects match your search criteria.</p>
                  <Button variant="outline-secondary" size="sm" onClick={() => setSearchTerm('')}>
                    Clear Search
                  </Button>
                </>
              ) : (
                <p>No projects found. Click "Add New Project" to create one.</p>
              )}
            </div>
          ) : (
            <Table responsive hover>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Project Name</th>
                  <th>Description</th>
                  <th>Assigned Developers</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredProjects.map((project, index) => (
                  <React.Fragment key={project.id}>
                    <tr>
                      <td>{index + 1}</td>
                      <td>{project.name}</td>
                      <td>{project.description || '-'}</td>
                      <td>
                        {project.developers && project.developers.length > 0 ? (
                          <div className="d-flex flex-wrap gap-1">
                            {project.developers.map(dev => (
                              <Badge key={dev.id} bg="secondary" className="me-1">
                                {dev.name}
                              </Badge>
                            ))}
                          </div>
                        ) : (
                          <span className="text-muted">No developers assigned</span>
                        )}
                      </td>
                      <td>
                        {new Date(project.created_at).toLocaleDateString()}
                      </td>
                      <td style={{ minWidth: '150px' }}>
                        <div className="d-flex align-items-center" style={{ gap: '0.5rem', whiteSpace: 'nowrap' }}>
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => toggleExpandProject(project.id)}
                            aria-expanded={expandedProjects[project.id]}
                          >
                            {expandedProjects[project.id] ? (
                              <i className="bi bi-chevron-up"></i>
                            ) : (
                              <i className="bi bi-chevron-down"></i>
                            )}
                          </Button>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleEditClick(project)}
                          >
                            <i className="bi bi-pencil"></i>
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDeleteClick(project)}
                          >
                            <i className="bi bi-trash"></i>
                          </Button>
                        </div>
                      </td>
                    </tr>
                    {expandedProjects[project.id] && (
                      <tr>
                        <td colSpan="6" className="p-0">
                          <div className="p-3">
                            <div className="border rounded p-3 mb-3">
                              <h5>Project Files and Resources</h5>
                              <Row>
                                <Col md={6}>
                                  <ProjectFiles projectId={project.id} />
                                </Col>
                                <Col md={6}>
                                  <ProjectUrls projectId={project.id} />
                                </Col>
                              </Row>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Add Project Modal */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Add New Project</Modal.Title>
        </Modal.Header>
        <Formik
          initialValues={{ name: '', description: '', developers: [] }}
          validationSchema={projectSchema}
          onSubmit={handleCreateProject}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            isSubmitting,
          }) => (
            <Form onSubmit={handleSubmit}>
              <Modal.Body>
                <Form.Group className="mb-3">
                  <Form.Label>Project Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={values.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.name && errors.name}
                    placeholder="Enter project name"
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.name}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Description (Optional)</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="description"
                    value={values.description}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Enter project description"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Assign Developers</Form.Label>
                  <div className="border rounded p-3" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                    {developers.map(developer => (
                      <Form.Check
                        key={developer.id}
                        type="checkbox"
                        id={`developer-${developer.id}`}
                        label={developer.name}
                        checked={values.developers.includes(developer.id)}
                        onChange={() => {
                          const isSelected = values.developers.includes(developer.id);
                          let newDevelopers;
                          
                          if (isSelected) {
                            // Remove developer if already selected
                            newDevelopers = values.developers.filter(id => id !== developer.id);
                          } else {
                            // Add developer if not selected
                            newDevelopers = [...values.developers, developer.id];
                          }
                          
                          // Update the form values
                          const event = {
                            target: {
                              name: 'developers',
                              value: newDevelopers
                            }
                          };
                          handleChange(event);
                        }}
                        className="mb-2"
                      />
                    ))}
                  </div>
                </Form.Group>
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={() => setShowAddModal(false)}>
                  Cancel
                </Button>
                <Button variant="primary" type="submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Adding...' : 'Add Project'}
                </Button>
              </Modal.Footer>
            </Form>
          )}
        </Formik>
      </Modal>

      {/* Edit Project Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Project</Modal.Title>
        </Modal.Header>
        {currentProject && (
          <Formik
            initialValues={{
              name: currentProject.name,
              description: currentProject.description || '',
              developers: currentProject.developers ? currentProject.developers.map(dev => dev.id) : []
            }}
            validationSchema={projectSchema}
            onSubmit={handleUpdateProject}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              isSubmitting,
            }) => (
              <Form onSubmit={handleSubmit}>
                <Modal.Body>
                  <Form.Group className="mb-3">
                    <Form.Label>Project Name</Form.Label>
                    <Form.Control
                      type="text"
                      name="name"
                      value={values.name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.name && errors.name}
                      placeholder="Enter project name"
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.name}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Description (Optional)</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      name="description"
                      value={values.description}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Enter project description"
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Assign Developers</Form.Label>
                    <div className="border rounded p-3" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                      {developers.map(developer => (
                        <Form.Check
                          key={developer.id}
                          type="checkbox"
                          id={`edit-developer-${developer.id}`}
                          label={developer.name}
                          checked={values.developers.includes(developer.id)}
                          onChange={() => {
                            const isSelected = values.developers.includes(developer.id);
                            let newDevelopers;
                            
                            if (isSelected) {
                              // Remove developer if already selected
                              newDevelopers = values.developers.filter(id => id !== developer.id);
                            } else {
                              // Add developer if not selected
                              newDevelopers = [...values.developers, developer.id];
                            }
                            
                            // Update the form values
                            const event = {
                              target: {
                                name: 'developers',
                                value: newDevelopers
                              }
                            };
                            handleChange(event);
                          }}
                          className="mb-2"
                        />
                      ))}
                    </div>
                  </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                  <Button variant="secondary" onClick={() => setShowEditModal(false)}>
                    Cancel
                  </Button>
                  <Button variant="primary" type="submit" disabled={isSubmitting}>
                    {isSubmitting ? 'Saving...' : 'Save Changes'}
                  </Button>
                </Modal.Footer>
              </Form>
            )}
          </Formik>
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={!!deleteConfirmation} onHide={() => setDeleteConfirmation(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {deleteConfirmation && (
            <p>
              Are you sure you want to delete the project <strong>"{deleteConfirmation.name}"</strong>?
              This action cannot be undone.
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDeleteConfirmation(null)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmDelete}>
            Delete Project
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
    </div>
  );
};

export default ManageProjects;
